import i18n from 'i18next';
import { isEmpty } from 'lodash/fp';
import React, { FC, useCallback, useMemo, useState } from 'react';
import Linkify from 'react-linkify';

declare global {
  interface Window {
    showDirectoryPicker(): Promise<FileSystemDirectoryHandle>;
  }
}

import { FilterTooltip } from 'components';
import { FlexBox } from 'components/CommunityMenu/CommunityMenuMembersList/styled';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { FileIcon, IconDownload, IconTick, Logo, MessagePiece } from 'static';
import { openModal } from 'store/actionCreators/modal';
import { ReadStatus, Type } from 'store/reducers/chat';
import { getSettings } from 'store/selectors/auth';
import { getPeerUsers } from 'store/selectors/chat';
import { ModalTypes } from 'types';
import { getNotificationDate } from 'utils/dates';

import ImageModal from './ImageModal/ImageModal';
import {
  AvatarContainer,
  Container,
  EyesIcon,
  FileContainer,
  FileName,
  FileType,
  IconWrapper,
  ImageContent,
  ImgWrapper,
  LogoText,
  MainContainer,
  MessageContainer,
  MessageContent,
  MessagePieceWrapper,
  MessageTime,
  SameAuthorMessageContent,
  SenderTitle,
  UerWrapper,
  Wrapper,
} from './styled';
import { validExtensions } from './utils';

interface MessageProps {
  content: string;
  sameAuthor: boolean;
  isGroup: boolean;
  date: string;
  userId: string;
  sameDay: boolean;
  time: string;
  urlUser: string;
  senderId: string;
  senderLastName: string;
  senderFirstName: string;
  isOpen: boolean;
  openViewMemberSidebar: (userId: string) => void;
  mediaType: Type;
  mediaUrl: string[];
  readStatus: ReadStatus[];
  isEveryoneRead: boolean;
}
const SeeMoreInnerComponent = ({
  readStatus,
  t,
}: {
  readStatus: ReadStatus[];
  t: TFunction;
}) => {
  const settings = useSelector(getSettings);
  const peerUsers = useSelector(getPeerUsers);
  const getUserProfile = (userId: string) =>
    peerUsers.find((user) => user.userId === userId);

  const filteredReadStatus = useMemo(() => {
    if (!readStatus) return [];
    const uniqueReadStatus = new Map();
    readStatus.forEach((status) => {
      if (status.userId) {
        uniqueReadStatus.set(status.userId, status);
      }
    });
    return Array.from(uniqueReadStatus.values());
  }, [readStatus]);

  return (
    <div>
      {filteredReadStatus?.length > 0 ? (
        filteredReadStatus.map(({ userId, readAt }) => {
          const userProfile = getUserProfile(userId);
          let name: string;
          if (!userProfile?.active && userId) {
            name = t('chat.popover.title');
          } else {
            name = `${
              userProfile.firstName.charAt(0).toUpperCase() +
                userProfile.firstName.slice(1).toLowerCase() || ''
            } ${
              userProfile.lastName.charAt(0).toUpperCase() +
                userProfile.lastName.slice(1).toLowerCase() || ''
            }`.trim();
          }

          return (
            <UerWrapper key={userId} className="divider-y">
              <UerWrapper.Text>
                {getNotificationDate(
                  t,
                  readAt,
                  name,
                  true,
                  settings.timeFormat,
                  i18n.language === 'en',
                )}
              </UerWrapper.Text>
            </UerWrapper>
          );
        })
      ) : (
        <UerWrapper>
          <UerWrapper.Text>{t('chat.noRead')}</UerWrapper.Text>
        </UerWrapper>
      )}
    </div>
  );
};
interface MediaRendererProps {
  mediaUrl: string;
  handleImageClick: (url: string, name: string) => void;
  fromModal?: boolean;
}

export const MediaRenderer: React.FC<MediaRendererProps> = ({
  mediaUrl,
  handleImageClick,
  fromModal,
}) => {
  const renderMediaContent = useMemo(() => {
    if (!mediaUrl) return null;

    const name = mediaUrl ? mediaUrl.split('/').pop() || '' : '';
    const type = name ? name.split('.').pop()?.toLowerCase() || '' : '';

    const isImage = validExtensions.includes(type);
    const isFile = !isImage;

    if (isImage) {
      return (
        <ImageContent
          src={mediaUrl}
          alt={name}
          onClick={() => handleImageClick(mediaUrl, name)}
          onError={(e) => {
            e.target.src = Logo;
            e.target.style.width = '120px';
            e.target.style.height = '120px';
            e.target.style.objectFit = 'cover';
          }}
        />
      );
    }

    if (isFile) {
      return (
        <FileContainer
          href={mediaUrl}
          target="_blank"
          rel="noreferrer"
          fromModal={fromModal}
        >
          <FileIcon width={26} height={26} />
          {name && <FileName>{name.substring(name.indexOf('_') + 1)}</FileName>}
          {type && <FileType>.{type}</FileType>}
        </FileContainer>
      );
    }

    return null;
  }, [mediaUrl, handleImageClick]);

  return renderMediaContent;
};

const Message: FC<MessageProps> = ({
  content,
  sameAuthor,
  isGroup,
  sameDay,
  time,
  urlUser,
  senderId,
  isOpen,
  senderFirstName,
  senderLastName,
  userId,
  openViewMemberSidebar,
  mediaUrl,
  readStatus,
  isEveryoneRead,
}) => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const [visible, setVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState('');
  const [modalCaption, setModalCaption] = useState('');
  const isReverse = senderId !== userId;
  const { t } = useTranslation();
  const handleDownloadImage = async (e: MouseEvent, url: string | string[]) => {
    e.stopPropagation();

    const downloadSingleImage = async (imageUrl: string) => {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const name = imageUrl ? imageUrl.split('/').pop() : '';

        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        addToast(t('errors.downloadFail'), {
          appearance: 'error',
          autoDismiss: true,
        });
        console.error('Download error:', err);
      }
    };

    if (Array.isArray(url)) {
      for (const imageUrl of url) {
        await downloadSingleImage(imageUrl);
      }
      addToast(t('Files downloaded successfully.'), {
        appearance: 'success',
        autoDismiss: true,
      });
    } else if (typeof url === 'string') {
      await downloadSingleImage(url);
      addToast(t('File downloaded successfully.'), {
        appearance: 'success',
        autoDismiss: true,
      });
    }
  };
  const linkifyDecorator = useCallback(
    (decoratedHref, decoratedText, key) => (
      <a
        target="_blank"
        rel="noopener noreferrer"
        href={decoratedHref}
        key={key}
      >
        {decoratedText}
      </a>
    ),
    [],
  );

  const messageContent = useMemo(
    () =>
      content && (
        <Linkify componentDecorator={linkifyDecorator}>
          <MessageContent>{content}</MessageContent>
        </Linkify>
      ),
    [content, linkifyDecorator],
  );

  const messageTime = useMemo(
    () => (
      <MessageTime reverse={isReverse}>
        <p>{time}</p>
        {!isReverse && (
          <FilterTooltip
            InnerComponent={
              <SeeMoreInnerComponent readStatus={readStatus} t={t} />
            }
            position="bottom-center"
            tooltipStyle={{
              maxWidth: 380,
              width: 'auto',
              maxHeight: 250,
              overflow: 'auto',
              zIndex: 150,
            }}
            isVisible={visible}
            closeTooltip={() => setVisible(false)}
          >
            <span
              onMouseEnter={() => !visible && setVisible(true)}
              style={{ cursor: 'pointer', display: 'flex', gap: '6px' }}
            >
              <IconTick
                width={14}
                height={10}
                color={isEmpty(readStatus) ? '#8298AB' : '#41A2EF'}
              />
              <EyesIcon color={isEveryoneRead ? '#41A2EF' : '#8298AB'} />
            </span>
          </FilterTooltip>
        )}
      </MessageTime>
    ),
    [isReverse, time, visible, readStatus],
  );

  const handleImageClick = (src: string, alt: string) => {
    setModalImage(src);
    setModalCaption(alt);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleSidebar = () => {
    dispatch(
      openModal({
        type: ModalTypes.FILE_VIEW,
        data: {
          urls: mediaUrl,
          handleDownloadImage: handleDownloadImage,
          handleImageClick: handleImageClick,
        },
      }),
    );
  };

  const mediaContent = useMemo(() => {
    if (Array.isArray(mediaUrl) && mediaUrl.length > 0) {
      if (mediaUrl.length > 1) {
        const firstImage = mediaUrl[0];
        return (
          <Container onClick={handleSidebar} minWidth="120px">
            <ImgWrapper>
              <p>{'+' + (mediaUrl.length - 1)}</p>
            </ImgWrapper>
            <IconWrapper
              onClick={(e: MouseEvent) => handleDownloadImage(e, mediaUrl)}
            >
              <IconDownload width="16" height="16" />
              <p>{t('common.download') + ' ' + t('common.all')}</p>
            </IconWrapper>
            <MediaRenderer mediaUrl={firstImage} handleImageClick={() => {}} />
          </Container>
        );
      } else {
        return mediaUrl.map((url, index) => (
          <Container key={index}>
            <IconWrapper onClick={(e) => handleDownloadImage(e, url)}>
              <IconDownload width="16" height="16" />
              <p>{t('common.download')}</p>
            </IconWrapper>
            <MediaRenderer mediaUrl={url} handleImageClick={handleImageClick} />
          </Container>
        ));
      }
    }
    return null;
  }, [mediaUrl, handleImageClick, handleDownloadImage, t]);

  const firstName = useMemo(
    () =>
      senderFirstName?.charAt(0)?.toUpperCase() +
        senderFirstName?.slice(1)?.toLowerCase() || '',
    [senderFirstName],
  );

  const lastName = useMemo(
    () =>
      senderLastName?.charAt(0)?.toUpperCase() +
        senderLastName?.slice(1)?.toLowerCase() || '',
    [senderLastName],
  );

  if (sameAuthor && sameDay) {
    return (
      <SameAuthorMessageContent reverse={isReverse} isOpen={isOpen}>
        <Wrapper reverse={isReverse} isOpen={isOpen}>
          <MessageContainer extraPadding={true} reverse={isReverse}>
            {isGroup && (
              <SenderTitle onClick={() => openViewMemberSidebar(senderId)}>
                {firstName + ' ' + lastName}
              </SenderTitle>
            )}
            <FlexBox gap={'10px'} style={{ flexDirection: 'column' }}>
              {mediaContent}
              {messageContent}
            </FlexBox>
          </MessageContainer>
        </Wrapper>

        {messageTime}
        <ImageModal
          isOpen={isModalOpen}
          imgSrc={modalImage}
          altText={modalCaption}
          onClose={closeModal}
        />
      </SameAuthorMessageContent>
    );
  }

  return (
    <MainContainer reverse={isReverse} isOpen={isOpen} aline={mediaUrl.length}>
      <Wrapper reverse={isReverse} isOpen={isOpen} aline={mediaUrl.length}>
        <MessagePieceWrapper reverse={isReverse}>
          <MessagePiece />
        </MessagePieceWrapper>
        <AvatarContainer
          extraHeight={false}
          onClick={() => openViewMemberSidebar(senderId)}
        >
          {isEmpty(urlUser) ? (
            <LogoText size="46px">
              {senderFirstName?.charAt(0) + senderLastName?.charAt(0)}
            </LogoText>
          ) : (
            <img src={urlUser} />
          )}
        </AvatarContainer>
        <MessageContainer reverse={isReverse}>
          {isGroup && (
            <SenderTitle onClick={() => openViewMemberSidebar(senderId)}>
              {firstName + ' ' + lastName}
            </SenderTitle>
          )}
          <FlexBox gap={'10px'} style={{ flexDirection: 'column' }}>
            {mediaContent}
            {messageContent}
          </FlexBox>
        </MessageContainer>
      </Wrapper>
      {messageTime}
      <ImageModal
        isOpen={isModalOpen}
        imgSrc={modalImage}
        altText={modalCaption}
        onClose={closeModal}
      />
    </MainContainer>
  );
};

export default React.memo(Message);
